import { memo, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { Switch, Route } from 'react-router'
import { Formik, Form } from 'formik'
import styled from 'styled-components'
import { equals } from 'ramda'
import config from '@/config'
import { colors, breakpoints, structure } from '@/styles'
import { select } from '@/store'
import AuthenticatedView from '@/authenticated-view'
import { toUnit } from '@/utils/styles'
import Notifications from '@/features/Settings/Notifications'
import Preferences from '@/features/Settings/Preferences'
import { Autosave } from '@/components/form'
import { toast } from '@/components/toast-notifications'
import ChangePhoneNumber from './ChangePhoneNumber'
import DeleteAccount from './DeleteAccount'
import EditSettings from './EditSettings'
import Feedback from './Feedback'
import PaymentSettings from './PaymentSettings'
import PlanSettings from './PlanSettings'
import SocialLoginPasword from './SocialLoginPassword'
import DevToolsPage from './DevTools'
import AutoReply from './AutoReply'
import SupportPage from '../Support'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  background: ${colors.white};
  min-height: calc(100vh - ${toUnit(structure.header.height)} - 50px);
  height: 100%;

  background-repeat: no-repeat;
  background-position: -43px 63px, 98% 55px, 100% 92%;
  background-size: 168px, 230px, 400px;

  @media screen and (max-width: ${breakpoints.dashboardMobile}) {
    background-repeat: no-repeat;
    background-position: -43px 63px, 98% 55px, 100% 98%;
    background-size: 168px, 230px, 250px;
  }

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    background-position: -70px 65px, 100% 70px, 100% 98%;
    background-size: 150px, 100px, 250px;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    min-height: calc(100vh - ${toUnit(structure.header.heightMobile)} - 50px);

    background-position: 100% 50px, 100% 98%;
    background-size: 100px, 170px;
  }
`

const Settings = ({ sidebarExtended }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const settings = useSelector(select.profile.settings)
  const isDev = config.BUILD_ENV === 'dev'

  const onSubmit = useCallback(
    async values => {
      if (equals(values, settings)) return
      const response = await dispatch.profile.updateSettings(values)
      if (!response?.ok) {
        toast(t('b.settings.view.settings.failed_save', 'Your settings could not be saved.'), {
          title: t('common.error', 'Error'),
          type: 'error'
        })
      }
    },
    [dispatch.profile, settings, t]
  )

  return (
    <>
      <Helmet>
        <title>{t('b.settings.view.settings.title', 'Settings')}</title>
      </Helmet>
      <Wrapper>
        <Switch>
          <Route
            exact
            path="/settings"
            render={props => <EditSettings sidebarExtended={sidebarExtended} {...props} />}
          />
          <AuthenticatedView
            path="/settings/payment-settings"
            render={props => <PaymentSettings sidebarExtended={sidebarExtended} {...props} />}
          />
          <AuthenticatedView
            path="/settings/plan-settings"
            allowTo={['landlord']}
            render={props => <PlanSettings sidebarExtended={sidebarExtended} {...props} />}
          />
          <Route
            exact
            path="/settings/change-phone-number"
            render={props => <ChangePhoneNumber sidebarExtended={sidebarExtended} {...props} />}
          />
          <AuthenticatedView
            path="/settings/auto-reply"
            allowTo={['landlord']}
            render={props => <AutoReply sidebarExtended={sidebarExtended} {...props} />}
          />
          <Route
            exact
            path="/settings/feedback"
            render={props => <Feedback sidebarExtended={sidebarExtended} {...props} />}
          />
          <AuthenticatedView
            exact
            path="/settings/delete-account"
            render={props => <DeleteAccount sidebarExtended={sidebarExtended} {...props} />}
          />
          <Route
            exact
            path="/settings/social-login-password"
            render={props => <SocialLoginPasword sidebarExtended={sidebarExtended} {...props} />}
          />
          <Route
            exact
            path="/settings/support"
            render={props => <SupportPage sidebarExtended={sidebarExtended} {...props} />}
          />
          {!!isDev && (
            <Route
              exact
              path="/settings/dev-tools"
              render={props => <DevToolsPage sidebarExtended={sidebarExtended} {...props} />}
            />
          )}
          <Formik initialValues={{ ...settings }} enableReinitialize>
            {({ values }) => (
              <Autosave values={values} onSave={onSubmit}>
                <Form>
                  <Route
                    exact
                    path="/settings/notifications"
                    render={props => <Notifications sidebarExtended={sidebarExtended} {...props} />}
                  />
                  <Route
                    exact
                    path="/settings/preferences"
                    render={props => <Preferences sidebarExtended={sidebarExtended} {...props} />}
                  />
                </Form>
              </Autosave>
            )}
          </Formik>
        </Switch>
      </Wrapper>
    </>
  )
}

export default memo(Settings)
